import React, { useMemo, useState, useEffect, useCallback } from "react";
import { API_BASE, toUrl } from "../../lib/config";
import bg from "../../assets/images/image.png"; // arka plan
import bg2 from "../../assets/images/megaparkdis.jpeg"; // arka plan

type Slide = {
  id: string;
  titleTop: string;
  titleBottom: string;
  subtitle: string;
  subtitle2?: string;
  imageUrl?: string;
  theme?: "default" | "alt"; // alt: üst başlık koyu, alt başlık yeşil
  ctas?: { label: string; variant: "primary" | "danger"; to?: string }[];
};

const HeroSlider: React.FC = () => {
  

  // Slaytları yalnızca API'den doldur
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  type ApiSlide = {
    id: string;
    page: string;
    kicker?: string | null;
    title?: string | null; // not used currently
    subtitle?: string | null;
    subtitle2?: string | null;
    titleTop?: string | null;
    titleBottom?: string | null;
    description?: string | null;
    imageUrl?: string | null;
    buttonLabel?: string | null;
    buttonHref?: string | null;
    overlayColor?: string | null;
    overlayOpacity?: string | null;
    accentColor?: string | null;
    textColor?: string | null;
    focus?: string | null;
    align?: string | null;
    theme?: "default" | "alt" | null;
    orderIndex?: number | null;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchSlides = async () => {
      try {
        setLoading(true); setError("");
        const res = await fetch(toUrl("/api/slider?page=home"));
        if (!res.ok) throw new Error("slider_fetch_failed");
        const data: ApiSlide[] = await res.json();
        console.log('[HeroSlider] Backend data:', data);
        const mapped: Slide[] = (Array.isArray(data) ? data : [])
          .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
          .map((s, idx) => {
            console.log('[HeroSlider] Mapping slide:', s.id, 'imageUrl:', s.imageUrl);
            return {
              id: s.id || `home-${idx}`,
              titleTop: s.titleTop || "",
              titleBottom: s.titleBottom || "",
              subtitle: s.subtitle || "",
              subtitle2: s.subtitle2 || undefined,
              imageUrl: s.imageUrl || undefined,
              theme: (s.theme === "alt" ? "alt" : "default"),
              // CTA'lar backend'den gelmediği için mevcut yapıyı koruyarak statik bırakıyoruz
              ctas: [
                { 
                  label: s.titleTop?.toLowerCase().includes("tüp bebek") ? "TÜP BEBEK" : "RANDEVU", 
                  variant: "primary", 
                  to: s.titleTop?.toLowerCase().includes("tüp bebek") ? "/tup-bebek" : "https://mgonline.hgkurumsal.com.tr/#/hizli-randevu" 
                },
                { label: "E-SONUÇ", variant: "danger", to: "https://mgonline.hgkurumsal.com.tr/#/lablogin" },
              ],
            };
          });
        if (!cancelled && mapped.length) setSlides(mapped);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || ""); // sessizce fallback kullan
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSlides();
    return () => { cancelled = true; };
  }, [API_BASE]);

  const [index, setIndex] = useState(0);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(i + 1, slides.length - 1));
  }, [slides.length]);

  const goPrevOrFirst = useCallback(() => setIndex(0), []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (slides.length ? (i + 1) % slides.length : 0));
    }, 10000);
    return () => clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft" && index === slides.length - 1) goPrevOrFirst();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrevOrFirst, index, slides.length]);

  return (
    <section
      className="hero-slider"
      aria-roledescription="carousel"
    >
      <div className="hero-track" style={{ transform: `translateX(-${index * 100}%)` }}>
        {slides.map((s, idx) => {
          // Backend resmi kullan, yoksa fallback
          const fallbackImage = idx % 2 === 0 ? bg : bg2;
          let finalImage = fallbackImage;
          
          if (s.imageUrl) {
            const baseIn = s.imageUrl;
            const uuidMatch = baseIn.match(/([0-9a-fA-F-]{36})$/);
            const raw = uuidMatch ? `/api/media/${uuidMatch[1]}`  : baseIn;
            finalImage = toUrl(raw);
          }
          
          return (
            <article 
              key={s.id} 
              className={`hero-slide ${s.theme === "alt" ? "theme-alt" : ""}`}
            >
              {finalImage && (
                <img 
                  src={finalImage} 
                  alt={s.titleTop || "Slider"}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: '65% center',
                    zIndex: 0
                  }}
                  crossOrigin="anonymous"
                />
              )}
              <div className="slide-inner container">
              <div className="copy">
                <em className="title-top">{s.titleTop}</em>
                <h2 className="title-bottom">{s.titleBottom}</h2>

                <div className="accent" aria-hidden="true" />

                <p className="subtitle">{s.subtitle}</p>
                {s.subtitle2 && <p className="subtitle subtitle-extra">{s.subtitle2}</p>}

                <div className="ctas">
                  {s.ctas?.map((c) => {
                    const href = c.to || "#";
                    const isExternal = /^https?:\/\//i.test(href);
                    return (
                      <a
                        key={c.label}
                        href={href}
                        className={`cta ${c.variant === "danger" ? "cta-danger" : "cta-primary"}`}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                      >
                        {c.label}
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </article>
          );
        })}
      </div>

      {/* Dots */}
      <div className="hero-dots">
        {slides.map((_, i) => (
          <button key={i} className={`dot ${i === index ? "active" : ""}`} onClick={() => setIndex(i)} aria-label={`Slide ${i + 1}`} />
        ))}
      </div>

      {/* Sağ ok her zaman */}
      <button className="hero-nav next" onClick={goNext} aria-label="Sonraki">
        <i className="bi bi-chevron-right" />
      </button>

      {/* Solda geri oku sadece son slaytta göster */}
      {index === slides.length - 1 && (
        <button className="hero-nav prev" onClick={goPrevOrFirst} aria-label="Başa dön">
          <i className="bi bi-chevron-left" />
        </button>
      )}
    </section>
  );
};

export default HeroSlider;

